import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import {
  getAdminEarningStatsApi,
  listCommissionRatesApi,
  addCommissionRateApi,
  activateCommissionRateApi,
  deactivateCommissionRateApi,
  deleteCommissionRateApi,
} from "../../../services/EarningsService";

const CommissionSettings = () => {
  const [rates, setRates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add-rate form
  const [form, setForm] = useState({ label: "", percentage: "" });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Action in-progress tracking
  const [activatingId, setActivatingId] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const activeRate = rates.find((r) => r.isActive) || null;

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [ratesRes, statsRes] = await Promise.all([
        listCommissionRatesApi(),
        getAdminEarningStatsApi(),
      ]);
      setRates(Array.isArray(ratesRes?.data?.data) ? ratesRes.data.data : []);
      setStats(statsRes?.data?.data || null);
    } catch {
      // silently fail; table shows empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Add rate ──────────────────────────────────────────────────────────────
  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError("");
    const pct = parseFloat(form.percentage);
    if (!form.label.trim()) {
      setFormError("Label is required.");
      return;
    }
    if (isNaN(pct) || pct < 0 || pct > 100) {
      setFormError("Percentage must be between 0 and 100.");
      return;
    }
    try {
      setSaving(true);
      await addCommissionRateApi({ label: form.label.trim(), percentage: pct });
      setForm({ label: "", percentage: "" });
      await fetchAll();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Failed to add rate.");
    } finally {
      setSaving(false);
    }
  };

  // ── Activate rate ─────────────────────────────────────────────────────────
  const handleActivate = async (rate) => {
    if (rate.isActive) return;
    const confirm = await Swal.fire({
      title: "Activate this rate?",
      html: `<b>${rate.label}</b> — <b>${rate.percentage}%</b> will become the active commission rate.<br/><small class="text-muted">All future session earnings will use this rate.</small>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, activate",
      confirmButtonColor: "#6571ff",
    });
    if (!confirm.isConfirmed) return;
    try {
      setActivatingId(rate.id);
      await activateCommissionRateApi(rate.id);
      await fetchAll();
      Swal.fire({ icon: "success", title: "Activated!", text: `${rate.label} (${rate.percentage}%) is now active.`, timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Failed to activate rate.", "error");
    } finally {
      setActivatingId(null);
    }
  };

  // ── Deactivate rate ───────────────────────────────────────────────────────
  const handleDeactivate = async (rate) => {
    const confirm = await Swal.fire({
      title: "Deactivate this rate?",
      html: `<b>${rate.label}</b> (${rate.percentage}%) will be deactivated.<br/><small class="text-muted">No commission will be deducted until another rate is activated.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate",
      confirmButtonColor: "#f72b50",
    });
    if (!confirm.isConfirmed) return;
    try {
      setDeactivatingId(rate.id);
      await deactivateCommissionRateApi(rate.id);
      await fetchAll();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Failed to deactivate rate.", "error");
    } finally {
      setDeactivatingId(null);
    }
  };

  // ── Delete rate ───────────────────────────────────────────────────────────
  const handleDelete = async (rate) => {
    if (rate.isActive) {
      Swal.fire("Cannot delete", "Activate another rate before deleting the active one.", "warning");
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete this rate?",
      text: `"${rate.label}" (${rate.percentage}%) will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#f72b50",
    });
    if (!confirm.isConfirmed) return;
    try {
      setDeletingId(rate.id);
      await deleteCommissionRateApi(rate.id);
      await fetchAll();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || "Failed to delete rate.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const pct = parseFloat(form.percentage);
  const previewValid = !isNaN(pct) && pct >= 0 && pct <= 100;

  return (
    <>
      <PageTitle activeMenu="Commission Settings" motherMenu="Finance" />

      {/* ── Stat Cards ── */}
      {stats && (
        <div className="row mb-4">
          {[
            { label: "Total Gross Revenue", value: `₹${Number(stats.totalGrossRevenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, sub: `${stats.totalSessionsBilled || 0} sessions`, icon: "fa-inr", color: "primary" },
            { label: "Commission Collected", value: `₹${Number(stats.totalCommissionCollected || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, sub: `${stats.currentCommissionPercentage || 0}% active rate`, icon: "fa-percent", color: "success" },
            { label: "Paid to Consultants", value: `₹${Number(stats.totalPaidToConsultants || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, sub: "Net earnings", icon: "fa-users", color: "info" },
            { label: "Pending Withdrawals", value: `₹${Number(stats.pendingWithdrawals?.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, sub: `${stats.pendingWithdrawals?.count || 0} requests`, icon: "fa-clock-o", color: "warning" },
          ].map((c) => (
            <div className="col-xl-3 col-sm-6" key={c.label}>
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className={`rounded-circle d-flex align-items-center justify-content-center bg-${c.color} text-white me-3`}
                      style={{ width: 52, height: 52, fontSize: 20, flexShrink: 0 }}>
                      <i className={`fa ${c.icon}`} />
                    </div>
                    <div>
                      <div className="text-muted small mb-1">{c.label}</div>
                      <div className="fs-4 fw-bold">{c.value}</div>
                      {c.sub && <div className="text-muted small">{c.sub}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="row">
        {/* ── Add Rate Form ── */}
        <div className="col-lg-4">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">Add Commission Rate</h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleAdd}>
                {formError && (
                  <div className="alert alert-danger py-2 small">{formError}</div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Label</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Standard Rate"
                    value={form.label}
                    onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Percentage (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="e.g. 20"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.percentage}
                    onChange={(e) => setForm((p) => ({ ...p, percentage: e.target.value }))}
                  />
                </div>

                {/* Live preview */}
                {previewValid && (
                  <div className="alert alert-info py-2 small mb-3">
                    <strong>Preview:</strong> On a ₹100 session — consultant gets{" "}
                    <strong>₹{(100 - pct).toFixed(2)}</strong>, platform gets{" "}
                    <strong>₹{pct.toFixed(2)}</strong>.
                  </div>
                )}

                <button type="submit" className="btn btn-primary w-100" disabled={saving}>
                  {saving ? (
                    <><span className="spinner-border spinner-border-sm me-2" />Adding...</>
                  ) : (
                    "Add Rate"
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* How it works */}
          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">How It Works</h4>
            </div>
            <div className="card-body">
              <ul className="list-unstyled mb-0" style={{ fontSize: 13 }}>
                {[
                  "Create multiple named commission rates.",
                  "Select (activate) one — it becomes the live rate instantly.",
                  "All new session earnings automatically use the active rate.",
                  "The active rate cannot be deleted; activate another first.",
                  "Existing consultant earnings are not retroactively changed.",
                ].map((t, i) => (
                  <li key={i} className="mb-2">
                    <i className="fa fa-check-circle text-success me-2" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Rates Table ── */}
        <div className="col-lg-8">
          {/* Active Rate Banner */}
          {activeRate && (
            <div className="alert alert-success d-flex align-items-center mb-3">
              <i className="fa fa-check-circle me-2 fs-5" />
              <div>
                <strong>Active Rate:</strong> {activeRate.label} —{" "}
                <strong>{activeRate.percentage}%</strong> is currently being deducted from consultant earnings.
              </div>
            </div>
          )}
          {!activeRate && !loading && (
            <div className="alert alert-warning d-flex align-items-center mb-3">
              <i className="fa fa-exclamation-triangle me-2 fs-5" />
              <div>No commission rate is active. Add a rate and activate it to start deducting commissions.</div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">Commission Rates</h4>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 40 }}>Active</th>
                      <th>Label</th>
                      <th>Rate</th>
                      <th>Consultant Gets</th>
                      <th>Platform Gets</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          <span className="spinner-border spinner-border-sm me-2 text-primary" />
                          Loading rates...
                        </td>
                      </tr>
                    )}
                    {!loading && rates.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-5 text-muted">
                          No commission rates added yet.
                        </td>
                      </tr>
                    )}
                    {!loading && rates.map((r) => (
                      <tr
                        key={r.id}
                        style={{
                          background: r.isActive ? "rgba(101,113,255,0.05)" : undefined,
                          opacity: activatingId && activatingId !== r.id ? 0.6 : 1,
                        }}
                      >
                        {/* Radio-style active indicator */}
                        <td className="text-center">
                          <button
                            className="btn btn-link p-0"
                            title={r.isActive ? "Click to deactivate" : "Click to activate"}
                            onClick={() => r.isActive ? handleDeactivate(r) : handleActivate(r)}
                            disabled={!!activatingId || !!deactivatingId || !!deletingId}
                          >
                            {(activatingId === r.id || deactivatingId === r.id) ? (
                              <span className="spinner-border spinner-border-sm text-primary" />
                            ) : r.isActive ? (
                              <i className="fa fa-dot-circle-o text-success fs-5" />
                            ) : (
                              <i className="fa fa-circle-o text-muted fs-5" />
                            )}
                          </button>
                        </td>

                        <td>
                          <span className="fw-semibold">{r.label}</span>
                          {r.isActive && (
                            <span className="badge badge-success light ms-2" style={{ fontSize: 10 }}>
                              ACTIVE
                            </span>
                          )}
                        </td>

                        <td>
                          <span className={`fw-bold fs-5 ${r.isActive ? "text-success" : "text-muted"}`}>
                            {r.percentage}%
                          </span>
                        </td>

                        <td className="text-muted small">
                          ₹{(100 - r.percentage).toFixed(2)} / ₹100
                        </td>

                        <td className="text-muted small">
                          ₹{Number(r.percentage).toFixed(2)} / ₹100
                        </td>

                        <td className="text-muted small">
                          {new Date(r.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })}
                        </td>

                        <td>
                          {r.isActive ? (
                            <button
                              className="btn btn-xs btn-danger light"
                              title="Deactivate this rate"
                              onClick={() => handleDeactivate(r)}
                              disabled={!!deactivatingId || !!activatingId}
                            >
                              {deactivatingId === r.id ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <><i className="fa fa-times me-1" />Deactivate</>
                              )}
                            </button>
                          ) : (
                            <>
                              <button
                                className="btn btn-xs btn-primary light me-1"
                                title="Set as active"
                                onClick={() => handleActivate(r)}
                                disabled={!!activatingId || !!deactivatingId || !!deletingId}
                              >
                                {activatingId === r.id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <><i className="fa fa-check me-1" />Activate</>
                                )}
                              </button>
                              <button
                                className="btn btn-xs btn-danger light"
                                title="Delete"
                                onClick={() => handleDelete(r)}
                                disabled={!!deletingId || !!activatingId || !!deactivatingId}
                              >
                                {deletingId === r.id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="fa fa-trash" />
                                )}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommissionSettings;
